import { FunctionComponent, useEffect, useRef, useState } from 'react'

import styled, { css } from 'styled-components'

import { getCurrentViewportType } from '@measures/responsive'
import translate from '@i18n'
import {
  SportCompetitorScheduleApiProps,
  SportCompetitorScheduleAPIResponse,
} from '@hooks/useSportEntitySchedule/types'
import useSportCompetitorSchedule from '@hooks/useSportEntitySchedule/useSportCompetitorSchedule'
import PrimaryCTAButton from '@components/Buttons/PrimaryCTAButton'
import Tile from '@components/RelatedMatches/Tile'

import SportEntitySchedulePlaceholder from './SportEntityPlaceholder'
import { getMostRecentItems, isMostRecentItem } from './utils'

const ShowAllGamesButton = styled(PrimaryCTAButton)`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    padding: ${spacing16};
  `}
`

const OuterWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing24 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    gap: ${spacing24};
    align-items: center;
  `}
`

const SportEntityScheduleWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    gap: ${spacing16};
    width: 100%;
  `}
`
const SportEntityScheduleGameWrapper = styled.div`
  width: 100%;
  > * {
    width: 100%;
  }
`

const SportCompetitorSchedule: FunctionComponent<
  SportCompetitorScheduleApiProps
> = ({ competitorId, entityType, typeOfSport }) => {
  const { data, isSuccess, error } = useSportCompetitorSchedule({
    entityId: competitorId,
    typeOfSport,
  })
  const [showAllGames, setShowAllGames] = useState(false)
  const [scheduleItems, setScheduleItems] = useState<
    SportCompetitorScheduleAPIResponse['items']
  >([])
  const ref = useRef<HTMLDivElement>(null)

  const items = data?.items
  const isDataEmpty = isSuccess && !items?.length

  const shouldShowMoreGamesButton =
    !showAllGames && items && items.length > 5 && entityType === 'competitor'

  const onClick = () => {
    const isDesktop = getCurrentViewportType() === 'desktop'
    setShowAllGames(true)
    setScheduleItems(items || [])

    if (isDesktop) {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }

  useEffect(() => {
    if (!!items?.length) {
      const limitedItems = getMostRecentItems(items)
      setScheduleItems(shouldShowMoreGamesButton ? limitedItems : items || [])
    }
  }, [items, shouldShowMoreGamesButton])

  return (
    <>
      <SportEntitySchedulePlaceholder error={error} isDataEmpty={isDataEmpty} />
      <OuterWrapper>
        <SportEntityScheduleWrapper>
          {scheduleItems?.map((item) => {
            return (
              <SportEntityScheduleGameWrapper
                key={`${item.typeOfSport}-${item.matchId}`}
                ref={isMostRecentItem(scheduleItems, item.matchId) ? ref : null}
              >
                <Tile
                  matchId={item.matchId}
                  typeOfSport={item.typeOfSport}
                  link={item.link}
                />
              </SportEntityScheduleGameWrapper>
            )
          })}
        </SportEntityScheduleWrapper>
        {shouldShowMoreGamesButton && (
          <ShowAllGamesButton
            colorVariant={'black'}
            size="small"
            onClick={onClick}
          >
            {translate('sportEntitySchedule.buttonText')}
          </ShowAllGamesButton>
        )}
      </OuterWrapper>
    </>
  )
}

export default SportCompetitorSchedule
